// Approvals queue — R-73–R-78.
// Keyboard: j/k move focus, a approve, d deny, ⌘Z undo within 10s (R-76).
// Rows pending >72 hours are flagged (R-78, SLA nudge).
// Private Relay addresses labeled clearly (R-74).

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type FormEvent,
} from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  listJoinRequests,
  approveJoinRequest,
  denyJoinRequest,
  type JoinRequest,
} from '../lib/api';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { timeAgo, hoursAgo } from '../lib/timeAgo';

const SLA_HOURS = 72;
const UNDO_MS = 10_000;

type PendingAction = {
  request: JoinRequest;
  action: 'approve' | 'deny';
  reason?: string;
  timeoutId: ReturnType<typeof setTimeout>;
  expiresAt: number;
};

export function ApprovalsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);
  const [denyModal, setDenyModal] = useState<{ request: JoinRequest } | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    listJoinRequests(user.orgId)
      .then((reqs) => {
        setRequests(reqs);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load join requests.');
        setLoading(false);
      });
  }, [user]);

  // Undo countdown ticker
  useEffect(() => {
    if (!pendingAction) return;
    const interval = setInterval(() => {
      const left = Math.ceil((pendingAction.expiresAt - Date.now()) / 1000);
      setUndoSecondsLeft(Math.max(0, left));
    }, 100);
    return () => clearInterval(interval);
  }, [pendingAction]);

  const commitAction = useCallback(
    async (pa: PendingAction) => {
      if (!user) return;
      try {
        if (pa.action === 'approve') {
          await approveJoinRequest(user.orgId, pa.request.id);
        } else {
          await denyJoinRequest(user.orgId, pa.request.id, pa.reason);
        }
      } catch {
        // Re-insert the request on failure so the admin can retry.
        setRequests((prev) => [pa.request, ...prev]);
      }
    },
    [user],
  );

  const scheduleAction = useCallback(
    (request: JoinRequest, action: 'approve' | 'deny', reason?: string) => {
      // Cancel any existing pending action first.
      if (pendingAction) {
        clearTimeout(pendingAction.timeoutId);
        commitAction(pendingAction);
      }

      // Optimistically remove from list.
      setRequests((prev) => prev.filter((r) => r.id !== request.id));

      const expiresAt = Date.now() + UNDO_MS;
      const timeoutId = setTimeout(() => {
        commitAction({ request, action, reason, timeoutId, expiresAt });
        setPendingAction(null);
      }, UNDO_MS);

      setPendingAction({ request, action, reason, timeoutId, expiresAt });
      setUndoSecondsLeft(Math.ceil(UNDO_MS / 1000));
    },
    [pendingAction, commitAction],
  );

  const undo = useCallback(() => {
    if (!pendingAction) return;
    clearTimeout(pendingAction.timeoutId);
    setRequests((prev) => [pendingAction.request, ...prev]);
    setPendingAction(null);
  }, [pendingAction]);

  const handleApprove = useCallback(
    (index: number) => {
      const req = requests[index];
      if (!req) return;
      scheduleAction(req, 'approve');
      // Move selection to same index (now pointing at next item).
      setSelectedIndex((i) => Math.min(i, requests.length - 2));
    },
    [requests, scheduleAction],
  );

  const handleDenyClick = useCallback(
    (request: JoinRequest) => {
      setDenyModal({ request });
      setDenyReason('');
    },
    [],
  );

  const handleDenySubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!denyModal) return;
      scheduleAction(denyModal.request, 'deny', denyReason.trim() || undefined);
      setDenyModal(null);
    },
    [denyModal, denyReason, scheduleAction],
  );

  useKeyboardNav({
    length: requests.length,
    selectedIndex,
    onSelect: setSelectedIndex,
    onApprove: handleApprove,
    onDeny: (i) => {
      const req = requests[i];
      if (req) handleDenyClick(req);
    },
    onUndo: undo,
    containerRef: listRef,
    enabled: !denyModal,
  });

  if (!user) return null;

  const slaBreached = (r: JoinRequest) => hoursAgo(r.requestedAt) > SLA_HOURS;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Approvals</h1>
          <p className="page-subtitle">
            Review and act on pending join requests for {user.orgName}.
          </p>
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <p
        style={{
          fontSize: '0.8125rem',
          color: 'var(--color-text-subtle)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <kbd style={kbdStyle}>j</kbd>/<kbd style={kbdStyle}>k</kbd> move
        &ensp;
        <kbd style={kbdStyle}>a</kbd> approve
        &ensp;
        <kbd style={kbdStyle}>d</kbd> deny
        &ensp;
        <kbd style={kbdStyle}>⌘Z</kbd> undo
      </p>

      {loading && (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
      )}

      {error && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}

      {!loading && !error && requests.length === 0 && !pendingAction && (
        <div className="empty-state">
          <p className="empty-state__title">No pending requests</p>
          <p className="empty-state__body">
            When someone requests to join {user.orgName}, they'll appear here.
          </p>
        </div>
      )}

      {!loading && requests.length > 0 && (
        <div
          ref={listRef}
          role="list"
          aria-label="Pending join requests"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
        >
          {requests.map((req, i) => (
            <RequestRow
              key={req.id}
              request={req}
              isSelected={i === selectedIndex}
              sla={slaBreached(req)}
              onSelect={() => setSelectedIndex(i)}
              onApprove={() => handleApprove(i)}
              onDeny={() => handleDenyClick(req)}
            />
          ))}
        </div>
      )}

      {/* Undo toast */}
      {pendingAction && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--space-6)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--color-sidebar-bg)',
            color: 'var(--color-sidebar-text)',
            padding: 'var(--space-3) var(--space-5)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            fontSize: '0.9375rem',
            zIndex: 100,
            whiteSpace: 'nowrap',
          }}
        >
          <span>
            {pendingAction.action === 'approve' ? 'Approved' : 'Denied'}{' '}
            <strong>{pendingAction.request.displayName}</strong>
          </span>
          <button
            onClick={undo}
            className="btn btn--sm"
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            Undo ({undoSecondsLeft}s)
          </button>
        </div>
      )}

      {/* Deny modal */}
      {denyModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="deny-dialog-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-6)',
            zIndex: 200,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDenyModal(null);
          }}
        >
          <div
            className="card"
            style={{ width: '100%', maxWidth: '440px', boxShadow: 'var(--shadow-md)' }}
          >
            <h2
              id="deny-dialog-title"
              style={{ fontSize: '1.125rem', marginBottom: 'var(--space-4)' }}
            >
              Deny {denyModal.request.displayName}?
            </h2>

            <form onSubmit={handleDenySubmit}>
              <div
                className="form-group"
                style={{ marginBottom: 'var(--space-5)' }}
              >
                <label className="form-label" htmlFor="deny-reason">
                  Reason (optional)
                </label>
                <textarea
                  id="deny-reason"
                  className="form-input"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  rows={3}
                  placeholder="Brief reason shown to the requester…"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                  autoFocus
                />
                <p className="form-hint">
                  The requester can re-apply after 7 days (R-19).
                </p>
              </div>

              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setDenyModal(null)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn--danger">
                  Deny request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// RequestRow
// ---------------------------------------------------------------------------

function RequestRow({
  request,
  isSelected,
  sla,
  onSelect,
  onApprove,
  onDeny,
}: {
  request: JoinRequest;
  isSelected: boolean;
  sla: boolean;
  onSelect: () => void;
  onApprove: () => void;
  onDeny: () => void;
}) {
  const methodLabel: Record<JoinRequest['method'], string> = {
    code:        'Join code',
    invitation:  'Invitation',
  };

  return (
    <div
      data-approval-row
      role="listitem"
      onClick={onSelect}
      style={{
        background: 'var(--color-surface)',
        border: `2px solid ${isSelected ? 'var(--color-accent)' : sla ? '#fbbf24' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius)',
        padding: 'var(--space-4)',
        cursor: 'pointer',
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
            {request.displayName}
          </span>
          {sla && (
            <span className="badge badge--warning" title="Waiting over 72 hours (R-78)">
              ⚠ SLA
            </span>
          )}
          {isSelected && (
            <span className="badge badge--accent">selected</span>
          )}
        </div>

        <div
          style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-muted)',
            marginTop: 'var(--space-1)',
            display: 'flex',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '0.8125rem',
            }}
          >
            {request.email}
          </span>
          {request.emailIsRelay && (
            <span
              className="badge badge--neutral"
              title="Apple Private Relay — cannot be used for domain matching (R-16)"
            >
              Private Relay
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-text-subtle)',
            marginTop: 'var(--space-2)',
            display: 'flex',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <span>{methodLabel[request.method]}</span>
          <span>Requested {timeAgo(request.requestedAt)}</span>
        </div>

        {request.message && (
          <blockquote
            style={{
              marginTop: 'var(--space-2)',
              paddingLeft: 'var(--space-3)',
              borderLeft: '3px solid var(--color-border)',
              fontStyle: 'italic',
              fontSize: '0.875rem',
              color: 'var(--color-text-muted)',
            }}
          >
            "{request.message}"
          </blockquote>
        )}
      </div>

      {/* Actions */}
      <div
        style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0, alignSelf: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="btn btn--primary btn--sm"
          onClick={onApprove}
          aria-label={`Approve ${request.displayName}`}
        >
          Approve
        </button>
        <button
          className="btn btn--secondary btn--sm"
          onClick={onDeny}
          aria-label={`Deny ${request.displayName}`}
        >
          Deny
        </button>
      </div>
    </div>
  );
}

const kbdStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 5px',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: '4px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.75rem',
  lineHeight: '1.6',
};
